import _ from 'lodash';
import {
  DatePicker,
  ExpansionPanels,
  GenericDialogLayout,
  HourPicker,
  StaffSelection, VcitaAlert, VcitaCheckbox, VcitaTextField
} from "@/modules/common/components";
import ServicesListSelect from "@/modules/bookings/components/common/ServicesListSelect/ServicesListSelect";
import LocationSelector from "@/modules/bookings/components/common/LocationSelector/LocationSelector";
import InteractionDetailsInput
  from "@/modules/bookings/components/common/InteractionDetailsInput/InteractionDetailsInput";
import FeeTypeGenerator from "@/modules/bookings/components/common/FeeTypeGenerator/FeeTypeGenerator";
import RecurrenceSelector from "@/modules/bookings/components/NewBookingDialog/RecurrenceSelector/RecurrenceSelector";
import {mapActions, mapGetters} from "vuex";
import {getStaffOfferedServices} from "@/modules/bookings/services/bookingsService";
import {zoomAuthorize} from "@/modules/common/services/zoomIntegrationService";
import {showSuccess} from "@/modules/common/services/frontageToastService";
import {bookingsService} from "@/modules/common/services";
import {convertTime12to24, convertTime24To12, getTimeObjWithOffset} from "@/modules/common/services/timeService";
import moment from "moment";

export const bookingMixin = {
  components: {
    GenericDialogLayout,
    ServicesListSelect,
    HourPicker,
    DatePicker,
    StaffSelection,
    ExpansionPanels,
    LocationSelector,
    InteractionDetailsInput,
    FeeTypeGenerator,
    VcitaTextField,
    RecurrenceSelector,
    VcitaCheckbox,
    VcitaAlert
  },
  props: {
    presetTimes: {
      type: Object,
      required: true,
    },
    meetingToCopy: {
      type: Object,
      required: false,
    },
    account: {
      type: Object,
      required: true,
    },
    conversation_uid: {
      type: String,
      default: '',
    },
    notes: {
      type: String,
      default: '',
    },
    isMobile: {
      type: Boolean,
      default: false,
    },
    service_uid: {
      type: String,
      default: '',
    },
    categories: {
      type: Array,
      required: false,
      default: () => ([])
    },
    staffs: {
      type: Array,
      required: false,
      default: () => ([])
    },
    additional_staffs: {
      type: String
    },
    staffUid: {
      type: String,
      default: '',
    },
  },
  watch: {
    'recurrenceParams.is_recurring': {
      immediate: true,
      async handler(newVal, oldVal) {
        this.redeemPackageSelected = this.canRedeemPackage && !newVal;
        this.newMeeting.require_confirmation = false
      },
    },
    'newMeeting.interaction_type': {
      async handler(newVal, oldVal) {
        this.shouldSubmitButtonBeDisabled = true
        try {
          await this.setDefaultInteractionDetails(newVal)
          await this.expandDetailsSectionIfNeeded(newVal)
        } finally {
          this.shouldSubmitButtonBeDisabled = false
        }

      }
    },
    selectedStaff: {
      async handler() {
        this.shouldSubmitButtonBeDisabled = true
        try {
          await this.loadStaffAvailabilities()
          await this.setDefaultInteractionDetails(this.newMeeting.interaction_type)
          await this.expandDetailsSectionIfNeeded(this.newMeeting.interaction_type)
        } finally {
          this.shouldSubmitButtonBeDisabled = false
        }

      },
    },
    additionalStaffs: {
      async handler() {
        await this.loadStaffAvailabilities()
      },
    },
    selectedService: {
      async handler(newVal) {
        if (!this.isAllDay) {
          this.modifyEndTimeWithDuration()
        }
        this.updateNewMeetingWithServiceDefaults()
        if (newVal === 'custom' || this.isDuplicated || this.newMeeting.interaction_type === 'client_location') {
          await this.$nextTick()
          this.$refs.interactionDetailsPanel.expandAll()
        }
      }
    },
    startTime: {
      handler(newVal, oldVal) {
        if (newVal && oldVal) {
          const newTime = this.getHourAndMinutesFromTime(convertTime12to24(newVal))
          const oldTime = this.getHourAndMinutesFromTime(convertTime12to24(oldVal))
          const duration = (newTime.hour - oldTime.hour) * 60 + (newTime.minutes - oldTime.minutes)
          const newEndTime = new Date(this.endTimeDateObject)
          this.updateEndTimeByDuration(duration, newEndTime)
        } else {
          this.handleEndDate()
        }
      },
    },
    startDate: {
      handler(newVal, oldVal) {
        if (newVal && oldVal) {
          const ONE_DAY = 1000 * 60 * 60 * 24
          const newDate = new Date(newVal)
          const oldDate = new Date(oldVal)
          const durationInDays = (newDate.getTime() - oldDate.getTime()) / ONE_DAY
          this.updateEndDateByDuration(durationInDays)
        }
        this.handleEndDate()
        this.loadStaffAvailabilities()
      }
    }
  },
  data() {
    const newMeeting = this.getDefaultBooking()
    const selectedService = null;
    const selectedStaff = null;
    return {
      newMeeting,
      meetingToDuplicate: this.meetingToCopy || null,
      recurrenceParams: {},
      recentlyPickedServices: null,
      endTime: '',
      selectedService,
      selectedStaff,
      additionalStaffs: this.additional_staffs?.split(', ') || [],
      parsedStaffAvailabilities: null,
      serverErrors: {},
      startDate: '',
      endDate: '',
      startTime: '',
      cachedAvailabilities: {},
      isSubmitting: false,
      offeredStaffServices: {},
      redeemPackageSelected: false,
      shouldDisplayNoteInput: false,
      firstFromCopy: false,
      customTitle: '',
      formattedRecurrenceParams: {},
      shouldSubmitButtonBeDisabled: false,
      didMount: false, // automation purposes

    }
  },
  methods: {
    ...mapActions("packagesModule", ["loadPackages"]),
    loadOfferedStaffServices() {
      if (!this.selectedService || this.offeredStaffServices[this.selectedStaff.uid + this.selectedService.uid] === 'making_call') {
        return
      }
      const offeredServiceId = this.selectedStaff.uid + this.selectedService.uid
      if (this.offeredStaffServices.hasOwnProperty(offeredServiceId)) {
        const cachedService = this.offeredStaffServices[offeredServiceId];
        return cachedService && cachedService.interaction_details;
      }
      this.offeredStaffServices[offeredServiceId] = 'making_call'

      if (this.newMeeting.type === 'appointment')
        return getStaffOfferedServices(this.selectedStaff.uid, this.selectedService.uid).then(res => {
          const offeredServices = res.data
          this.offeredStaffServices[offeredServiceId] = offeredServices[0]
          return offeredServices[0] && offeredServices[0].interaction_details
        }).catch(e => console.error(e))
    },
    handleZoomIntegration() {
      zoomAuthorize().then((res) => {
        if (res === 'window-closed') {
          return
        }
        showSuccess(this.$t('settings.integrations_page.zoom.connection_success'))
        this.selectedStaff.zoom_connected = true
      }).catch(() => {
        this.newMeeting.interaction_type = this.selectedService.interaction_type || "online"
      })
    },
    handleLocationSelect(val) {
      this.newMeeting.interaction_type = val
      if (this.isDuplicated) {
        if (this.meetingToDuplicate.interaction_type === 'default_business_location') {
          this.meetingToDuplicate.interaction_type = 'business_location'
        }
        if (val !== this.meetingToDuplicate.interaction_type) {
          this.deleteCopy()
        }
      }
    },
    async expandDetailsSectionIfNeeded(interactionType) {
      if (!this.selectedService || !interactionType) {
        return;
      }
      if ((interactionType !== 'zoom' && !this.newMeeting.interaction_details) || this.shouldDisplayZoomWarning) {
        await this.$nextTick()
        this.$refs.interactionDetailsPanel.expandAll()
      }
    },
    async setDefaultInteractionDetails(interactionType) {
      if (!this.selectedService || !interactionType) {
        return
      }
      // Copy details from meetingToDuplicate
      if (this.isDuplicated) {
        this.newMeeting.interaction_details = this.meetingToDuplicate.interaction_details
        this.newMeeting = {...this.newMeeting}
        return
      }

      let res = '';
      switch (interactionType) {
        case 'business_location':
        case 'default_business_location':
          this.newMeeting.interaction_type = 'business_location'
          interactionType = this.newMeeting.interaction_type
          res = this.account.business.address
          break
        case 'client_location':
          let address_field = this.matterFields.find(element => element.type === 'address');
          if (address_field) {
            let matter_address_field = this.matter.fields.find(element => element.uid === address_field.id);
            if (matter_address_field && matter_address_field.value)
              res = matter_address_field.value
            else
              res = this.client.address
          } else
            res = this.client.address
          break
        case 'client_phone':
          res = this.client.phone_to_display
          break
        case 'business_phone':
          res = this.account.business.phone
          break
      }

      // Set the interaction details from the staff location
      // Don't enter if the call from duplicate meeting
      if (!this.isDuplicated
        && (interactionType !== 'client_location' && interactionType !== 'client_phone')) {
        res = await this.getServiceSpecificInteractionDetails(interactionType) || res
      }
      this.newMeeting.interaction_details = res
      this.newMeeting = {...this.newMeeting}
    },
    async loadStaffAvailabilities() {
      if (!this.isStartTimeValid || !this.isEndTimeValid) {
        return
      }
      const currDate = new Date(this.startDate)
      const currDateMonth = currDate.getMonth() + 1
      let nextDayDate = new Date(currDate.getFullYear(), currDate.getMonth(), currDate.getDate() + 1);
      const nextDayMonth = nextDayDate.getMonth() + 1
      let startDateString = `${currDate.getFullYear()}-${currDateMonth.toString().padStart(2, '0')}-${currDate.getDate()}`;
      let endDateString = `${nextDayDate.getFullYear()}-${nextDayMonth.toString().padStart(2, '0')}-${nextDayDate.getDate()}`;
      const staffIds = [this.selectedStaff.uid, ...this.additionalStaffs]

      const availabilitiesId = `${startDateString}-${staffIds.join('-')}`
      if (this.cachedAvailabilities[availabilitiesId]) {
        this.parsedStaffAvailabilities = this.cachedAvailabilities[availabilitiesId];
        return;
      }
      const type = this.newMeeting.type === 'appointment' ? 'AppointmentService' : 'EventService'
      const availabilities = await bookingsService.getAvailability(null, startDateString, endDateString, this.staff.time_zone_name, staffIds, null, 'staff', type)
      //filter for the current day
      const staffAvailabilities = {}
      _.forEach(availabilities, (staffAvailability, staffId) => {
        staffAvailabilities[staffId] = _.flatMap(staffAvailability, (dateObj, date) => new Date(date).getTime() === currDate.getTime() ? dateObj : [])
      })

      //check the staffAvailabilities is not empty
      if (Object.keys(staffAvailabilities).length === 0) {
        this.parsedStaffAvailabilities = []
        this.cachedAvailabilities[availabilitiesId] = [];
        return;
      }

      const parsedAvailabilities = {}
      // set the start & end time for each staffAvailabilities
      _.forEach(staffAvailabilities, (availabilities, staffId) => {
        if (!availabilities.length) {
          parsedAvailabilities[staffId] = []
        } else {
          _.forEach(availabilities, availability => {
            const startTime = getTimeObjWithOffset(new Date(availability.start_time), this.staff.time_zone_offset)
            const endTime = new Date(startTime)
            endTime.setMinutes(endTime.getMinutes() + availability.duration_minutes)

            if (!parsedAvailabilities[staffId]) {
              parsedAvailabilities[staffId] = []
            }
            const relatedTimeCheck = parsedAvailabilities[staffId].find(timeSlot => timeSlot.endTime.getTime() === startTime.getTime())
            if (relatedTimeCheck) {
              relatedTimeCheck.endTime = endTime
            } else {
              parsedAvailabilities[staffId].push({
                startTime,
                endTime
              })
            }
          })
        }
      })
      this.parsedStaffAvailabilities = parsedAvailabilities
      this.cachedAvailabilities[availabilitiesId] = parsedAvailabilities;
    },
    handleClose(data = null) {
      this.$emit('close', data)
    },
    handleLoaderTimeOut() {
      this.isSubmitting = false
    },
    interactionTypeMatchesService(interactionType) {
      return interactionType === this.selectedService.interaction_type
        || (interactionType === 'business_location')
    },
    async getServiceSpecificInteractionDetails(interactionType) {
      let res = ''
      if (this.interactionTypeMatchesService(interactionType)) {
        if (this.selectedService.meeting_interaction_details) {
          res = this.selectedService.meeting_interaction_details
        }
        const offeredStaffServiceDetails = await this.loadOfferedStaffServices()
        if (offeredStaffServiceDetails) {
          res = offeredStaffServiceDetails
        }
      }
      return res
    },
    deleteCopy() {
      this.meetingToDuplicate = null
    },
    initializeSelectedService() {
      const preselectedServiceUid = this.service_uid || (this.meetingToDuplicate && this.meetingToDuplicate.service_uid)
      if (this.isDuplicated) {
        this.selectedService = this.servicesMap[preselectedServiceUid]
        if (this.meetingToDuplicate.service_uid === 'custom') {
          this.customTitle = this.meetingToDuplicate.title
          this.selectedService = this.meetingToDuplicate.service_uid
        }
        this.selectedStaff = this.meetingToDuplicate.staff
        this.newMeeting = {...this.meetingToDuplicate}
        return
      }

      if (preselectedServiceUid) {
        this.selectedService = this.servicesMap[preselectedServiceUid]
      }
      if (this.modifiedCategories.length === 1 && this.modifiedCategories[0].services.length === 1) {
        this.selectedService = this.modifiedCategories[0].services[0]
      }
    },
    /**
     * In case of a duplicate a custom event
     * @param meet
     */
    getSpecificDuplicateFields(meet) {
      if (this.isDuplicated && this.meetingToDuplicate) {
        meet.charge_type = this.meetingToDuplicate.charge_type
        meet.price = this.meetingToDuplicate.price
        meet.currency = this.meetingToDuplicate.currency
        meet.max_attendance = this.meetingToDuplicate.max_attendance
        meet.interaction_type = this.meetingToDuplicate.interaction_type
      }
    },
    getPresetTimes(times) {
      if (!times) {
        times = this.presetTimes
      }
      if (this.newMeeting.type === 'appointment') {
        this.setPresetTime()
      }
      let startTime = `${times.startTime.getHours().toString().padStart(2, '0')}:${times.startTime.getMinutes().toString().padStart(2, '0')}`
      let endTime = `${times.endTime.getHours().toString().padStart(2, '0')}:${times.endTime.getMinutes().toString().padStart(2, '0')}`

      if (this.amPmMode) {
        startTime = convertTime24To12(startTime)
        endTime = convertTime24To12(endTime)
      }
      const startDate = moment(times.startTime).format('YYYY-MM-DD')
      const endDate = moment(times.endTime).format('YYYY-MM-DD')
      return {
        startDate,
        endDate,
        startTime,
        endTime,
      }
    },
    testAmPm(val) {
      const regexAmPm = /^([0]?[1-9]|1[0-2]):([0-5]\d)\s?(AM|PM)$/i;
      return regexAmPm.test(val)
    },
    test24(val) {
      const regex24 = /^([0-9]|0[0-9]|1[0-9]|2[0-3]):[0-5][0-9]$/;
      return regex24.test(val)
    },
    addDays(dateObj, days = 1) {
      dateObj.setDate(dateObj.getDate() + days);
    },
    handleEndDate() {
      const startDate = new Date(this.startDate)
      const endDate = new Date(this.endDate)
      if (startDate > endDate) {
        this.endDate = this.startDate;
        if (this.startTimeDateObject > this.endTimeDateObject) {
          this.modifyEndTimeWithDuration();
        }
      }
    },
    modifyEndTimeWithDuration() {
      if (!this.isStartTimeValid) {
        return;
      }
      const duration = this.getDuration()
      const newEndTime = new Date(this.startTimeDateObject)
      this.updateEndTimeByDuration(duration, newEndTime)
      if (!this.isAllDay) {
        this.endDate = moment(newEndTime).format('YYYY-MM-DD')
      }
    },
    getHourAndMinutesFromTime(time) {
      const res = (time.match(/.{1,5}/g))[0].split(':')
      return {
        hour: res[0],
        minutes: res[1]
      }
    },
    updateEndTimeByDuration(duration, newEndTime) {
      newEndTime.setMinutes(newEndTime.getMinutes() + duration)
      let endTime = `${newEndTime.getHours().toString().padStart(2, '0')}:${newEndTime.getMinutes().toString().padStart(2, '0')}`
      if (this.amPmMode) {
        endTime = convertTime24To12(endTime)
      }
      this.endTime = endTime
    },
    updateEndDateByDuration(durationInDays) {
      this.endDate = moment(this.endDate).add(durationInDays, 'day').format('YYYY-MM-DD')
    },
    getDuration() {
      let duration = 60
      if (this.selectedService && this.selectedService.duration > 0) {
        duration = this.selectedService.duration
      }
      if (this.isAllDay) {
        duration = 24 * 60
      }
      if (this.meetingToDuplicate && this.meetingToDuplicate.duration > 0) {
        duration = this.meetingToDuplicate.duration
      }
      return duration
    },
    setSelectedStaff() {
      if (this.staffs && this.staffs.length) {
        if (this.staffUid) {
          this.selectedStaff = this.staffs.find(staff => staff.uid === this.staffUid)
        }
      } else {
        const unwatchStaffs = this.$watch('staffs', () => {
          if (this.staffUid) {
            this.selectedStaff = this.staffs.find(staff => staff.uid === this.staffUid)
          }
          unwatchStaffs()
        })
      }
    },
    handleAdditionalStaff(additionalStaffs) {
      this.additionalStaffs = additionalStaffs
      if (this.additionalStaffs.length) {
        this.newMeeting.require_confirmation = false
      }
    }
  },
  computed: {
    ...mapGetters("packagesModule", ["packages"]),
    ...mapGetters('accountModule', ['staff']),
    ...mapGetters('mattersModule', ['matter']),
    ...mapGetters('clientFields', ['matterFields']),

    shouldDisplayZoomWarning() {
      return this.newMeeting.interaction_type === 'zoom' && !this.selectedStaff.zoom_connected
    },
    isSelectedStaffAccountStaff() {
      return this.selectedStaff.uid === this.account.staff.uid
    },
    startTimeDateObject() {
      let dateString = `${this.startDate} ${this.amPmMode && this.startTime ? convertTime12to24(this.startTime) : this.startTime}`
      return moment(dateString).toDate()
    },
    endTimeDateObject() {
      let dateString = `${this.endDate} ${this.amPmMode && this.endTime ? convertTime12to24(this.endTime) : this.endTime}`
      return moment(dateString).toDate()
    },
    isStartTimeValid() {
      return !isNaN(this.startTimeDateObject.getTime())
    },
    isEndTimeValid() {
      return !isNaN(this.endTimeDateObject.getTime())
    },
    shouldDisplayPrimeStaffDoubleBookingWarning() {
      if (!this.isStartTimeValid || !this.isEndTimeValid || !this.parsedStaffAvailabilities || this.shouldDisplayTimeInPastWarning || this.isAllDay) {
        return false;
      }
      if (Object.keys(this.parsedStaffAvailabilities).length === 0) {
        return true
      }
      const primeStaffAvailability = _.find(this.parsedStaffAvailabilities, (_, staffId) => staffId === this.selectedStaff.uid)
      if (!primeStaffAvailability) {
        return false
      }
      const that = this
      return !primeStaffAvailability.some(availability => availability.startTime <= that.startTimeDateObject && availability.endTime >= that.endTimeDateObject)
    },
    additionalStaffsAvailabilityWarning() {
      const res = {}

      if (!this.isStartTimeValid || !this.isEndTimeValid || !this.parsedStaffAvailabilities || this.shouldDisplayTimeInPastWarning || this.isAllDay) {
        return res;
      }
      if (Object.keys(this.parsedStaffAvailabilities).length === 0) {
        this.additionalStaffs.forEach(staffId => res[staffId] = true)
        return res
      }

      this.additionalStaffs.forEach(staffId => res[staffId] = false)

      const that = this
      _.forEach(this.parsedStaffAvailabilities, (staffAvailability, staffId) => {
        if (staffId !== this.selectedStaff.uid) {
          res[staffId] = !staffAvailability.some(availability => {
            return availability.startTime <= that.startTimeDateObject && availability.endTime >= that.endTimeDateObject
          })
        }
      })
      return res
    },
    shouldDisplayTimeInPastWarning() {
      return this.startTimeDateObject < new Date()
    },
    shouldDisplayEndFollowingDayWarning() {
      return this.startTimeDateObject > this.endTimeDateObject
    },
    servicesMap() {
      return this.categories.reduce((acc, cat) => {

        const catServicesMap = cat.services.reduce((map, service) => {
          map[service.id] = service
          return map
        }, {})
        return {
          ...acc,
          ...catServicesMap
        }
      }, {})
    },
    isPaidMeeting() {
      const charge_type = this.newMeeting.charge_type
      return !!charge_type && ['paid_non_secured', 'paid_force', 'paid'].indexOf(charge_type) !== -1
    },
    amPmMode() {
      return this.account.business.time_format === '12h'
    },
    enableMultiStaff() {
      return this.staffs.length
    },
    shouldDisplayStaffSelect() {
      return this.staffs.length > 1 && ['admin', 'manager', 'collaborator'].indexOf(this.account.staff.role) !== -1 && ['admin', 'manager', 'collaborator'].indexOf(this.staff.role) !== -1;
    },
    startTimeRules() {
      const that = this
      return [
        val => !!val || that.$t('common.errors.required_word'),
        val => that.testAmPm(val) || that.test24(val) || that.$t('common.errors.hour')
      ]
    },
    endTimeRules() {
      const that = this
      return [
        val => !!val || that.$t('booking.validation_errors.required_input'),
        val => that.testAmPm(val) || that.test24(val) || that.$t('common.errors.hour')
      ]
    },
    dateRules() {
      const that = this
      return [
        val => !!val || that.$t('booking.validation_errors.required_input'),
        val => {
          const isValidDate = !isNaN(new Date(val).getTime())
          return isValidDate || that.$t('common.errors.date')
        },
      ]
    },
    serviceRules() {
      const that = this
      return [
        val => !!val || that.$t('booking.validation_errors.required_input'),
      ]
    },
    interactionDetailsRules() {
      const that = this
      return [
        val => !!val || that.$t('booking.validation_errors.required_input'),
      ]
    },
    customTitleRules() {
      const that = this
      return [
        val => !that.isSelectedServiceCustom || !!val || that.$t('booking.validation_errors.required_input'),
      ]
    },
    locationRules() {
      const that = this
      return [
        val => !that.isSelectedServiceCustom || !!val || that.$t('booking.validation_errors.required_input'),
      ]
    },
    isSelectedServiceCustom() {
      return this.selectedService === 'custom'
    },
    isDuplicated() {
      return !!(this.meetingToDuplicate && this.meetingToDuplicate.service_uid)
    },
    newMeetingCurrencySymbol() {
      return this.$options.filters.currency(this.newMeeting.currency)
    },
    isAdditionalStaffsAvailabilityWarning() {
      return _.some(this.additionalStaffsAvailabilityWarning, staff => staff)
    },
    showAvailabilityWarning() {
      return this.isAdditionalStaffsAvailabilityWarning || this.shouldDisplayPrimeStaffDoubleBookingWarning
    },
    doubleBookingWarning() {
      let primeStaff = null
      if (this.shouldDisplayPrimeStaffDoubleBookingWarning) {
        primeStaff = this.selectedStaff.display_name
      }
      let additionalStaff = []
      if (this.isAdditionalStaffsAvailabilityWarning) {
        additionalStaff = this.staffs.flatMap(staff => {
          if (this.additionalStaffsAvailabilityWarning[staff.uid]) {
            return staff.display_name
          }
          return []
        })
      }
      let lastUnavailable = ''
      let schedulingConflict = 'booking.validation_errors.new_scheduling_conflict_for_one'
      if (primeStaff && additionalStaff.length || additionalStaff.length > 1) {
        lastUnavailable = `${additionalStaff.pop()}`
        schedulingConflict = 'booking.validation_errors.new_scheduling_conflict'
      }
      const unavailableStaffList = primeStaff ? [primeStaff, ...additionalStaff].join(', ') : [...additionalStaff].join(', ')
      return this.$t(schedulingConflict, {unavailable_staff_list: unavailableStaffList, last_unavailable: lastUnavailable})
    },
  }
}
